import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Table,
  Button,
  Input,
  Modal,
  Form,
  Row,
  Col,
  Avatar,
  Tag,
  Dropdown,
  Statistic,
  Tabs,
  List,
  Typography,
  message,
} from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  EditOutlined,
  DeleteOutlined,
  MoreOutlined,
  UserOutlined,
  PhoneOutlined,
  MailOutlined,
  HomeOutlined,
  ShoppingOutlined,
  DollarOutlined,
} from '@ant-design/icons';
import useStore from '../store/useStore';
import { customersAPI } from '../services/api';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorDisplay from '../components/common/ErrorDisplay';
import FormField from '../components/forms/FormField';
import RightDrawer from '../components/common/RightDrawer';
import DetailView, { DetailSection, DetailField } from '../components/common/DetailView';

const { Search } = Input;
const { Text } = Typography;


const Customers = () => {
  const { customers, setCustomers } = useStore();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [modalVisible, setModalVisible] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [currentView, setCurrentView] = useState('list'); // 'list' | 'detail'
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerOrders, setCustomerOrders] = useState([]);
  const [form] = Form.useForm();

  const fetchCustomers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await customersAPI.getAll();
      setCustomers(response.data);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCustomerOrders = async (customerId) => {
    try {
      const response = await customersAPI.getOrders(customerId);
      setCustomerOrders(response.data);
    } catch (err) {
      console.error('Failed to fetch customer orders:', err);
    }
  };

  const handleSearch = (value) => {
    setSearchText(value);
  };

  const filteredCustomers = customers.filter(customer =>
    customer.name.toLowerCase().includes(searchText.toLowerCase()) ||
    customer.email.toLowerCase().includes(searchText.toLowerCase()) ||
    customer.phone?.includes(searchText)
  );

  const handleAddCustomer = () => {
    setEditingCustomer(null);
    form.resetFields();
    setDrawerVisible(true);
  };

  const handleEditCustomer = (customer) => {
    setEditingCustomer(customer);
    form.setFieldsValue(customer);
    setDrawerVisible(true);
  };

  const handleViewCustomer = async (customer) => {
    setSelectedCustomer(customer);
    setCurrentView('detail');
    await fetchCustomerOrders(customer.id);
  };

  const handleDeleteCustomer = async (customerId) => {
    try {
      await customersAPI.delete(customerId);
      await fetchCustomers();
      message.success('Customer deleted successfully');
    } catch (err) {
      message.error('Failed to delete customer');
    }
  };

  const handleDrawerClose = () => {
    setDrawerVisible(false);
    setEditingCustomer(null);
    form.resetFields();
  };

  const handleDrawerSave = async () => {
    try {
      setDrawerLoading(true);
      const values = await form.validateFields();
      
      if (editingCustomer) {
        await customersAPI.update(editingCustomer.id, values);
        message.success('Customer updated successfully');
      } else {
        await customersAPI.create(values);
        message.success('Customer created successfully');
      }
      
      await fetchCustomers();
      handleDrawerClose();
    } catch (err) {
      message.error('Failed to save customer');
    } finally {
      setDrawerLoading(false);
    }
  };

  const handleBackToList = () => {
    setCurrentView('list');
    setSelectedCustomer(null);
    setCustomerOrders([]);
  };

  const getCustomerStatus = (customer) => {
    if (customer.totalOrders === 0) return { color: 'default', text: 'New' };
    if (customer.totalSpent > 10000) return { color: 'gold', text: 'VIP' };
    if (customer.totalSpent > 5000) return { color: 'blue', text: 'Premium' };
    return { color: 'green', text: 'Regular' };
  };

  const columns = [
    {
      title: 'Customer',
      key: 'customer',
      render: (_, record) => (
        <div className="flex items-center gap-3">
          <Avatar
            src={record.avatar}
            icon={<UserOutlined />}
            size="large"
          />
          <div>
            <div className="font-medium">{record.name}</div>
            <div className="text-sm text-gray-500">{record.email}</div>
          </div>
        </div>
      ),
    },
    {
      title: 'Contact',
      key: 'contact',
      render: (_, record) => (
        <div className="space-y-1">
          <div className="flex items-center gap-1 text-sm">
            <PhoneOutlined className="text-gray-400" />
            <span>{record.phone || 'N/A'}</span>
          </div>
          <div className="flex items-center gap-1 text-sm">
            <HomeOutlined className="text-gray-400" />
            <span>{record.city || 'N/A'}</span>
          </div>
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (_, record) => {
        const status = getCustomerStatus(record);
        return <Tag color={status.color}>{status.text}</Tag>;
      },
    },
    {
      title: 'Total Orders',
      dataIndex: 'totalOrders',
      key: 'totalOrders',
      sorter: (a, b) => a.totalOrders - b.totalOrders,
    },
    {
      title: 'Total Spent',
      dataIndex: 'totalSpent',
      key: 'totalSpent',
      render: (amount) => `$${amount?.toFixed(2) || '0.00'}`,
      sorter: (a, b) => a.totalSpent - b.totalSpent,
    },
    {
      title: 'Last Order',
      dataIndex: 'lastOrderDate',
      key: 'lastOrderDate',
      render: (date) => date ? new Date(date).toLocaleDateString() : 'Never',
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Dropdown
          menu={{
            items: [
              {
                key: 'view',
                icon: <UserOutlined />,
                label: 'View Details',
                onClick: () => handleViewCustomer(record),
              },
              {
                key: 'edit',
                icon: <EditOutlined />,
                label: 'Edit',
                onClick: () => handleEditCustomer(record),
              },
              {
                key: 'orders',
                icon: <ShoppingOutlined />,
                label: 'View Orders',
                onClick: () => handleViewCustomer(record),
              },
              {
                type: 'divider',
              },
              {
                key: 'delete',
                icon: <DeleteOutlined />,
                label: 'Delete',
                danger: true,
                onClick: () => {
                  Modal.confirm({
                    title: 'Delete Customer',
                    content: 'Are you sure you want to delete this customer?',
                    onOk: () => handleDeleteCustomer(record.id),
                  });
                },
              },
            ],
          }}
          trigger={['click']}
        >
          <Button type="text" icon={<MoreOutlined />} />
        </Dropdown>
      ),
    },
  ];

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  if (loading) {
    return <LoadingSpinner size="large" tip="Loading customers..." />;
  }

  if (error) {
    return (
      <ErrorDisplay
        error={error}
        onRetry={fetchCustomers}
        title="Failed to load customers"
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-slate-100">Customers</h1>
          <p className="text-gray-600">Manage your customer relationships</p>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleAddCustomer}
          size="large"
        >
          Add Customer
        </Button>
      </div>

      {/* Quick Stats */}
      {currentView === 'list' && (
      <Row gutter={16}>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Total Customers"
              value={customers.length}
              prefix={<UserOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="VIP Customers"
              value={customers.filter(c => getCustomerStatus(c).text === 'VIP').length}
              prefix={<UserOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="New Customers"
              value={customers.filter(c => getCustomerStatus(c).text === 'New').length}
              prefix={<UserOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
      </Row>
      )}

      {/* Search */}
      {currentView === 'list' && (
      <Card>
        <Search
          placeholder="Search customers by name, email, or phone..."
          allowClear
          onSearch={handleSearch}
          onChange={(e) => handleSearch(e.target.value)}
          style={{ maxWidth: 400 }}
          enterButton={<SearchOutlined />}
        />
      </Card>
      )}

      {/* Customers Table */}
      <Card>
        {currentView === 'list' ? (
          <Table
            dataSource={filteredCustomers}
            columns={columns}
            rowKey="id"
            onRow={(record) => ({
              onClick: () => handleViewCustomer(record),
              className: 'cursor-pointer hover:bg-gray-50'
            })}
            pagination={{
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} customers`,
            }}
            scroll={{ x: 800 }}
          />
        ) : (
          selectedCustomer && (
            <DetailView
              title={selectedCustomer.name}
              subtitle={selectedCustomer.email}
              onBack={handleBackToList}
              onEdit={() => handleEditCustomer(selectedCustomer)}
              onDelete={() => {
                Modal.confirm({
                  title: 'Delete Customer',
                  content: 'Are you sure you want to delete this customer?',
                  onOk: () => handleDeleteCustomer(selectedCustomer.id),
                });
              }}
              tags={[{ label: getCustomerStatus(selectedCustomer).text, color: getCustomerStatus(selectedCustomer).color }]}
            >
              <Row gutter={24}>
                <Col xs={24} lg={16} xl={16}>
                  <Tabs
                    defaultActiveKey="profile"
                    items={[
                      {
                        key: 'profile',
                        label: 'Profile',
                        children: (
                          <DetailSection title="Customer Information">
                            <DetailField label="Full Name" value={selectedCustomer.name} />
                            <DetailField label="Email" value={selectedCustomer.email} />
                            <DetailField label="Phone" value={selectedCustomer.phone || 'N/A'} />
                            <DetailField label="Company" value={selectedCustomer.company || 'N/A'} />
                            {selectedCustomer.address && (
                              <DetailField label="Address" value={`${selectedCustomer.address}${selectedCustomer.city ? `, ${selectedCustomer.city}` : ''}${selectedCustomer.state ? `, ${selectedCustomer.state}` : ''}${selectedCustomer.zipCode ? ` ${selectedCustomer.zipCode}` : ''}`} />
                            )}
                            <DetailField label="Status" value={getCustomerStatus(selectedCustomer).text} />
                            {selectedCustomer.notes && (<DetailField label="Notes" value={selectedCustomer.notes} />)}
                          </DetailSection>
                        )
                      },
                      {
                        key: 'orders',
                        label: 'Orders',
                        children: (
                          <DetailSection title="Order History">
                            <List
                              dataSource={customerOrders}
                              renderItem={(order) => (
                                <List.Item>
                                  <List.Item.Meta
                                    title={`Order #${order.id}`}
                                    description={
                                      <div className="space-y-1">
                                        <div>Date: {new Date(order.orderDate).toLocaleDateString()}</div>
                                        <div>Status: <Tag color="blue">{order.status}</Tag></div>
                                        <div>Total: ${order.total?.toFixed(2)}</div>
                                      </div>
                                    }
                                  />
                                </List.Item>
                              )}
                              locale={{ emptyText: 'No orders found' }}
                            />
                          </DetailSection>
                        )
                      }
                    ]}
                  />
                </Col>
                <Col xs={24} lg={8} xl={8}>
                  <DetailSection title="Quick Stats">
                    <Statistic title="Total Orders" value={selectedCustomer.totalOrders || 0} prefix={<ShoppingOutlined />} />
                    <Statistic title="Total Spent" value={selectedCustomer.totalSpent || 0} prefix={<DollarOutlined />} precision={2} />
                  </DetailSection>
                </Col>
              </Row>
            </DetailView>
          )
        )}
      </Card>

      {/* Right Drawer for Add/Edit */}
      <RightDrawer
        title={editingCustomer ? 'Edit Customer' : 'Add New Customer'}
        open={drawerVisible}
        onClose={handleDrawerClose}
        loading={drawerLoading}
        width={800}
      >
        <Form form={form} layout="vertical" className="mt-2" onFinish={handleDrawerSave} id="drawer-form">
          <Row gutter={16}>
            <Col span={12}>
              <FormField
                name="name"
                label="Full Name"
                type="input"
                required
                placeholder="Enter customer name"
              />
            </Col>
            <Col span={12}>
              <FormField
                name="email"
                label="Email"
                type="email"
                required
                placeholder="Enter email address"
              />
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <FormField
                name="phone"
                label="Phone"
                type="input"
                placeholder="Enter phone number"
              />
            </Col>
            <Col span={12}>
              <FormField
                name="company"
                label="Company"
                type="input"
                placeholder="Enter company name"
              />
            </Col>
          </Row>

          <FormField
            name="address"
            label="Address"
            type="textarea"
            placeholder="Enter full address"
          />

          <Row gutter={16}>
            <Col span={8}>
              <FormField
                name="city"
                label="City"
                type="input"
                placeholder="Enter city"
              />
            </Col>
            <Col span={8}>
              <FormField
                name="state"
                label="State"
                type="input"
                placeholder="Enter state"
              />
            </Col>
            <Col span={8}>
              <FormField
                name="zipCode"
                label="ZIP Code"
                type="input"
                placeholder="Enter ZIP code"
              />
            </Col>
          </Row>

          <FormField
            name="notes"
            label="Notes"
            type="textarea"
            placeholder="Additional notes about the customer"
          />
        </Form>
      </RightDrawer>
    </div>
  );
};

export default Customers;
